import { useEffect, useState } from 'react';
import { ImageWithCrops, ReservationSummaryDto } from '@/services/platform/api/model';
import {
  ImageMap,
  ImagesToImageMap,
  useLocaleStore,
  formatDate,
  formatTimeFromDate,
  timePartIconFromDate,
  AppContext,
  useClientTranslation,
} from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { eventTitle } from '@/services/platform/data/booking/bookingTitle';
import { Icon } from '@/components/ui/icon';
import { Link } from 'react-router-dom';

export interface BookingCardProps {
  booking: ReservationSummaryDto;
  context?: AppContext | undefined;
}

export function BookingCard({ booking, context }: BookingCardProps) {
  const translation = useClientTranslation();
  const { appSettings } = useApp();

  // Non-null only for event bookings, where the card is titled by the event rather than by the
  // "{category} at {venue}" shape — an event's venue is the room it happens in, not its identity.
  const title = eventTitle(booking);
  const venueName = booking.venue?.name ?? booking.name ?? booking.serviceCategory?.name ?? '—';
  const venueImage =
    booking.serviceType === 'Event'
      ? booking.imageUrl
        ? { src: booking.imageUrl, alt: title ?? venueName }
        : ImagesToImageMap(appSettings?.properties?.eventNoPicImage, 'bookingListImage')
      : booking.serviceType == 'Driver'
        ? ImageMap(booking.serviceCategory?.image as ImageWithCrops, 'bookingListImage')
        : ImageMap(booking.venue?.image as ImageWithCrops, 'bookingListImage');
  const locale = useLocaleStore(state => state.locale);
  const category = booking.serviceCategory?.name;
  const date = formatDate(booking.startDate, locale);
  const time = formatTimeFromDate(booking.startDate);
  const timeIcon = timePartIconFromDate(booking.startDate);
  const guests = booking.guestCount;

  const bookingDetailBaseUrl = context?.settings?.properties?.bookingDetail?.route?.path;
  const bookingUrl = `${bookingDetailBaseUrl}/${booking?.publicGuid}`;
  const bookingStatus = booking.status?.toLowerCase() ?? '';

  // Fall back to the neutral placeholder if the image is missing or fails to load.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [venueImage?.src]);
  const showImage = !!venueImage && !imageFailed;

  return (
    <article className="flex flex-col">
      <Link to={bookingUrl} className="flex flex-col gap-2.5">
        <div className="bg-jncquoi-neutral-100 relative h-58 w-full overflow-hidden">
          {showImage ? (
            <img
              src={venueImage.src}
              alt={venueName}
              className="absolute inset-0 size-full object-cover"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="bg-jncquoi-neutral-300 absolute inset-0" />
          )}

          {bookingStatus === 'pending' && (
            <>
              <div aria-hidden="true" className="absolute inset-0 bg-[#1A1A1A99]" />
              <span className="font-inter absolute top-2.5 left-2.5 inline-flex items-center bg-black/80 px-3 py-1.5 text-[10px] font-medium tracking-[0.5px] text-white uppercase backdrop-blur-[5px]">
                {translation('App.Labels.PendingConfirmation')}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <p className="font-pp-neue-montreal text-[20px] leading-normal text-black">
            {title ? (
              <span className="font-normal">{title}</span>
            ) : (
              <>
                {category && <span className="font-semibold">{category} </span>}
                {booking?.serviceType != 'Driver' && (
                  <span className="font-normal">
                    {translation('App.Labels.BookingAtVenue')} {venueName}
                  </span>
                )}
              </>
            )}
          </p>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
            {date && (
              <div className="flex items-center gap-2">
                <Icon
                  name="Calendar"
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                  {date}
                </span>
              </div>
            )}
            {time && (
              <div className="flex items-center gap-2">
                <Icon
                  name={timeIcon}
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                  {time}
                </span>
              </div>
            )}
            {guests != null && (
              <div className="flex items-center gap-2">
                <Icon
                  name="Group"
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] whitespace-nowrap">
                  {guests} {guests === 1 ? translation('App.Labels.Guest') : translation('App.Labels.Guests')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
